import { useState } from 'react';
import { useTranslation } from 'react-i18next';

function MockupDashboard() {
  const [view, setView] = useState('balance'); // balance | categories

  return (
    <div className="space-y-3 select-none">
      {/* Tab toggle */}
      <div className="flex gap-1 bg-gray-100 dark:bg-gray-700 rounded-lg p-0.5">
        {['balance', 'categories'].map((v) => (
          <button
            key={v}
            onClick={() => setView(v)}
            className={`flex-1 text-[9px] font-semibold py-1 rounded-md transition-all ${
              view === v
                ? 'bg-white dark:bg-gray-600 text-gray-900 dark:text-white shadow-sm'
                : 'text-gray-400'
            }`}
          >
            {v === 'balance' ? 'Balance' : 'Categorias'}
          </button>
        ))}
      </div>

      {view === 'balance' ? (
        <>
          <div className="text-lg font-extrabold text-gray-900 dark:text-white">
            $2,924,500
          </div>
          <div className="flex gap-3 text-[10px]">
            <span className="text-emerald-500 font-semibold">+$4,300,000</span>
            <span className="text-red-400 font-semibold">-$1,375,500</span>
          </div>
          {/* Mini sparkline */}
          <div className="flex items-end gap-[3px] h-8 pt-1">
            {[40, 25, 55, 35, 60, 45, 70].map((h, i) => (
              <div
                key={i}
                className="flex-1 rounded-sm bg-brand-400/60 dark:bg-brand-500/40 transition-all hover:bg-brand-500"
                style={{ height: `${h}%` }}
              />
            ))}
          </div>
        </>
      ) : (
        <div className="space-y-2">
          {[
            { name: 'Comida', pct: 65, color: 'bg-amber-400', amount: '$390k' },
            { name: 'Transporte', pct: 40, color: 'bg-blue-400', amount: '$240k' },
            { name: 'Vivienda', pct: 87, color: 'bg-purple-400', amount: '$520k' },
          ].map((item) => (
            <div key={item.name}>
              <div className="flex justify-between text-[9px] mb-1">
                <span className="text-gray-500 dark:text-gray-400">{item.name}</span>
                <span className="font-bold text-gray-500 dark:text-gray-400">{item.amount}</span>
              </div>
              <div className="h-1.5 bg-gray-100 dark:bg-gray-700 rounded-full overflow-hidden">
                <div
                  className={`h-full ${item.color} rounded-full transition-all duration-700`}
                  style={{ width: `${item.pct}%` }}
                />
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function MockupTransaction() {
  const allTx = [
    { icon: '🍽️', name: 'Supermercado', date: 'Hoy', amount: '-$85,500', color: 'text-red-400', bg: 'bg-amber-100 dark:bg-amber-500/20' },
    { icon: '💰', name: 'Salario', date: 'Feb 1', amount: '+$3,500,000', color: 'text-emerald-500', bg: 'bg-emerald-100 dark:bg-emerald-500/20' },
    { icon: '🏠', name: 'Arriendo', date: 'Feb 1', amount: '-$450,000', color: 'text-red-400', bg: 'bg-purple-100 dark:bg-purple-500/20' },
    { icon: '🚗', name: 'Uber', date: 'Feb 3', amount: '-$12,000', color: 'text-red-400', bg: 'bg-blue-100 dark:bg-blue-500/20' },
    { icon: '🎬', name: 'Netflix', date: 'Feb 5', amount: '-$15,990', color: 'text-red-400', bg: 'bg-pink-100 dark:bg-pink-500/20' },
  ];

  const [dismissed, setDismissed] = useState([]);
  const visible = allTx.filter((_, i) => !dismissed.includes(i)).slice(0, 3);

  return (
    <div className="space-y-2 select-none">
      <div className="flex justify-between items-center mb-1">
        <span className="text-[9px] text-gray-400 uppercase tracking-wider font-semibold">Movimientos</span>
        {dismissed.length > 0 && (
          <button
            onClick={() => setDismissed([])}
            className="text-[9px] text-brand-500 hover:text-brand-600 font-semibold"
          >
            Restaurar
          </button>
        )}
      </div>
      {visible.map((tx, vi) => {
        const realIndex = allTx.indexOf(tx);
        return (
          <div
            key={tx.name + tx.date}
            onClick={() => setDismissed((prev) => [...prev, realIndex])}
            className="flex items-center gap-2.5 cursor-pointer rounded-lg p-1 -m-1 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-all group"
            title="Click para descartar"
          >
            <div className={`w-8 h-8 rounded-lg ${tx.bg} flex items-center justify-center text-sm`}>
              {tx.icon}
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-xs font-semibold text-gray-900 dark:text-white truncate">
                {tx.name}
              </div>
              <div className="text-[10px] text-gray-400">{tx.date}</div>
            </div>
            <div className={`text-xs font-bold ${tx.color}`}>{tx.amount}</div>
          </div>
        );
      })}
    </div>
  );
}

function MockupGoal() {
  const [saved, setSaved] = useState(800);
  const target = 2000;
  const pct = Math.min(Math.round((saved / target) * 100), 100);
  const isComplete = saved >= target;

  return (
    <div className="space-y-3 select-none">
      <div className="flex items-center gap-2">
        <span className="text-lg">✈️</span>
        <div className="flex-1">
          <div className="text-xs font-bold text-gray-900 dark:text-white">
            Vacaciones
          </div>
          <div className="text-[10px] text-gray-400">${saved.toLocaleString()} / ${target.toLocaleString()}</div>
        </div>
      </div>
      <div className="h-2 bg-gray-100 dark:bg-gray-700 rounded-full overflow-hidden">
        <div
          className={`h-full rounded-full transition-all duration-500 ${isComplete ? 'bg-emerald-400' : 'bg-gradient-to-r from-brand-500 to-accent-500'}`}
          style={{ width: `${pct}%` }}
        />
      </div>
      <button
        onClick={() => setSaved((s) => Math.min(s + 200, target))}
        disabled={isComplete}
        className={`w-full text-[10px] font-semibold py-1.5 rounded-lg transition-all ${
          isComplete
            ? 'bg-emerald-100 dark:bg-emerald-500/10 text-emerald-600 dark:text-emerald-400'
            : 'bg-brand-50 dark:bg-brand-500/10 text-brand-600 dark:text-brand-400 hover:bg-brand-100 dark:hover:bg-brand-500/20 active:scale-[0.97]'
        }`}
      >
        {isComplete ? 'Completada!' : `+ Ahorrar $200`}
      </button>

      {/* Static completed goal */}
      <div className="pt-1 border-t border-gray-100 dark:border-gray-700">
        <div className="flex items-center gap-2">
          <span className="text-lg">💻</span>
          <div>
            <div className="text-xs font-bold text-gray-900 dark:text-white">Laptop nueva</div>
            <div className="text-[10px] text-emerald-500 font-semibold">Completada!</div>
          </div>
        </div>
        <div className="h-2 bg-gray-100 dark:bg-gray-700 rounded-full overflow-hidden mt-1.5">
          <div className="h-full w-full bg-emerald-400 rounded-full" />
        </div>
      </div>
    </div>
  );
}

export default function HeroSection({ onCta }) {
  const { t } = useTranslation();

  return (
    <section className="pt-32 pb-20 px-4">
      <div className="max-w-4xl mx-auto text-center">
        <h1 className="text-4xl sm:text-5xl lg:text-6xl font-display font-extrabold text-gray-900 dark:text-white leading-tight">
          {t('landing.hero.title')}
          <br />
          <span className="text-gradient">{t('landing.hero.titleHighlight')}</span>
        </h1>
        <p className="mt-6 text-lg sm:text-xl text-gray-600 dark:text-gray-400 max-w-2xl mx-auto">
          {t('landing.hero.subtitle')}
        </p>
        <div className="mt-10 flex flex-col sm:flex-row gap-4 justify-center">
          <button onClick={onCta} className="btn-primary text-lg py-4 px-8">
            {t('landing.hero.cta')}
          </button>
          <a href="#how-it-works" className="btn-secondary text-lg py-4 px-8">
            {t('landing.hero.ctaSecondary')}
          </a>
        </div>

        {/* Mockup cards */}
        <div className="mt-16 flex justify-center gap-4 sm:gap-6 max-w-3xl mx-auto">
          <div className="animate-float hidden sm:block w-48 sm:w-56 rounded-2xl shadow-lg bg-white dark:bg-gray-800 p-4 border border-gray-100 dark:border-gray-700 -rotate-2">
            <MockupDashboard />
          </div>
          <div className="animate-float-delayed w-56 sm:w-60 rounded-2xl shadow-xl bg-white dark:bg-gray-800 p-4 border border-gray-100 dark:border-gray-700 z-10 scale-105">
            <MockupTransaction />
          </div>
          <div className="animate-float-slow hidden sm:block w-48 sm:w-56 rounded-2xl shadow-lg bg-white dark:bg-gray-800 p-4 border border-gray-100 dark:border-gray-700 rotate-2">
            <MockupGoal />
          </div>
        </div>
      </div>
    </section>
  );
}
